export enum Capacity {
  DEVELOPERS = 'developers',
  NUMBER_OF_APPS = 'numberOfApps',
  ENVIRONMENTS = 'environments',
  CLOUD_STORAGE_GB = 'cloudStorageGB',
  CLOUD_REGIONS = 'cloudRegions'
}

export interface CapacityTypeMap {
  [Capacity.DEVELOPERS]: number | 'unlimited';
  [Capacity.NUMBER_OF_APPS]: number | 'unlimited';
  [Capacity.ENVIRONMENTS]: string[];
  [Capacity.CLOUD_STORAGE_GB]: number | 'unlimited';
  [Capacity.CLOUD_REGIONS]: string[];
}

export enum Capability {
  FULLSTACK_DEVELOPMENT_PLATFORM = 'fullstackDevelopmentPlatform',
  CLOUDCODE_SERVERLESS_INTEGRATIONS = 'cloudcodeServerlessIntegrations',
  POWERSYNC_INTEGRATION = 'powerSyncIntegration',
  STANDARD_SECURITY_FEATURES = 'standardSecurityFeatures',
  CUSTOM_BRANDING = 'customBranding',
  SSO_INTEGRATION = 'ssoIntegration',
  GITHUB_VERSION_CONTROL = 'githubVersionControl',
  CLOUDCODE_WEB_TASKS = 'cloudcodeWebTasks',
  NFC_ENABLED_APPS = 'nfcEnabledApps',
  SQL_DATA_PIPELINES = 'sqlDataPipelines',
  ADDITIONAL_SECURITY = 'additionalSecurity',
  ADVANCED_HARDWARE_CAPABILITIES = 'advancedHardwareCapabilities',
  MODEL_VIEWER_3D = 'threeDModelViewer',
  POWERBI_DASHBOARDS = 'powerBIDashboards',
  FINE_GRAINED_PERMISSIONS = 'fineGrainedPermissions',
  APP_PACKAGES = 'appPackages'
}

export type CapabilityTypeMap = {
  [key in Capability]: boolean | string;
};

export enum Support {
  SLA = 'sla',
  ONBOARDING_SESSION = 'onboardingSession',
  DISCORD_SUPPORT = 'discordSupport',
  DOCS = 'docs',
  HELPDESK = 'helpdesk',
  CLOUD_UPTIME_GUARANTEE = 'cloudUptimeGuarantee',
  CUSTOMER_SUCCESS_MANAGER = 'customerSuccessManager',
  INCIDENT_RESPONSE = 'incidentResponse',
  INCIDENT_RESOLUTION_TIME = 'incidentResolutionTime'
}

export type SupportTypeMap = {
  [key in Support]: boolean | string;
};

export interface PricingPlan {
  name: string;
  description: string;
  pricePerAppUserPerMonth: number | [number, number];
  priceFootnote: string;
  capacities: CapacityTypeMap;
  capabilities: CapabilityTypeMap;
  support: SupportTypeMap;
  startBuilding: boolean;
}

export const PricingPlans: PricingPlan[] = [
  {
    name: 'Starter',
    description: 'Build a single app with local impact',
    pricePerAppUserPerMonth: 10,
    priceFootnote: 'Billed annually',
    startBuilding: true,
    capacities: {
      [Capacity.DEVELOPERS]: 1,
      [Capacity.NUMBER_OF_APPS]: 1,
      [Capacity.ENVIRONMENTS]: ['Dev', 'Staging', 'Production'],
      [Capacity.CLOUD_STORAGE_GB]: 'unlimited',
      [Capacity.CLOUD_REGIONS]: ['US', 'EU', 'APAC']
    },
    capabilities: {
      [Capability.FULLSTACK_DEVELOPMENT_PLATFORM]: true,
      [Capability.CLOUDCODE_SERVERLESS_INTEGRATIONS]: true,
      [Capability.POWERSYNC_INTEGRATION]: true,
      [Capability.STANDARD_SECURITY_FEATURES]: true,
      [Capability.CUSTOM_BRANDING]: false,
      [Capability.SSO_INTEGRATION]: false,
      [Capability.GITHUB_VERSION_CONTROL]: false,
      [Capability.CLOUDCODE_WEB_TASKS]: false,
      [Capability.NFC_ENABLED_APPS]: false,
      [Capability.SQL_DATA_PIPELINES]: 'From $150 pm',
      [Capability.ADDITIONAL_SECURITY]: false,
      [Capability.ADVANCED_HARDWARE_CAPABILITIES]: false,
      [Capability.MODEL_VIEWER_3D]: false,
      [Capability.POWERBI_DASHBOARDS]: '$120 pm',
      [Capability.FINE_GRAINED_PERMISSIONS]: false,
      [Capability.APP_PACKAGES]: 'Limited to 1'
    },
    support: {
      [Support.SLA]: true,
      [Support.ONBOARDING_SESSION]: true,
      [Support.DISCORD_SUPPORT]: true,
      [Support.DOCS]: true,
      [Support.HELPDESK]: true,
      [Support.CLOUD_UPTIME_GUARANTEE]: '99%',
      [Support.CUSTOMER_SUCCESS_MANAGER]: false,
      [Support.INCIDENT_RESPONSE]: 'Business Day',
      [Support.INCIDENT_RESOLUTION_TIME]: false
    }
  },
  {
    name: 'Professional',
    description: 'Build multiple apps for a medium-sized user base',
    pricePerAppUserPerMonth: 25,
    priceFootnote: 'Billed annually',
    startBuilding: true,
    capacities: {
      [Capacity.DEVELOPERS]: 'unlimited',
      [Capacity.NUMBER_OF_APPS]: 'unlimited',
      [Capacity.ENVIRONMENTS]: ['Dev', 'Staging', 'Production'],
      [Capacity.CLOUD_STORAGE_GB]: 'unlimited',
      [Capacity.CLOUD_REGIONS]: ['US', 'EU', 'APAC']
    },
    capabilities: {
      [Capability.FULLSTACK_DEVELOPMENT_PLATFORM]: true,
      [Capability.CLOUDCODE_SERVERLESS_INTEGRATIONS]: true,
      [Capability.POWERSYNC_INTEGRATION]: true,
      [Capability.STANDARD_SECURITY_FEATURES]: true,
      [Capability.CUSTOM_BRANDING]: true,
      [Capability.SSO_INTEGRATION]: 'Included with limitations. Contact us for details.',
      [Capability.GITHUB_VERSION_CONTROL]: true,
      [Capability.CLOUDCODE_WEB_TASKS]: true,
      [Capability.NFC_ENABLED_APPS]: true,
      [Capability.SQL_DATA_PIPELINES]: true,
      [Capability.ADDITIONAL_SECURITY]: false,
      [Capability.ADVANCED_HARDWARE_CAPABILITIES]: false,
      [Capability.MODEL_VIEWER_3D]: false,
      [Capability.POWERBI_DASHBOARDS]: '$120 pm',
      [Capability.FINE_GRAINED_PERMISSIONS]: false,
      [Capability.APP_PACKAGES]: 'Limited to 3 / app'
    },
    support: {
      [Support.SLA]: true,
      [Support.ONBOARDING_SESSION]: true,
      [Support.DISCORD_SUPPORT]: true,
      [Support.DOCS]: true,
      [Support.HELPDESK]: true,
      [Support.CLOUD_UPTIME_GUARANTEE]: '99.9%',
      [Support.CUSTOMER_SUCCESS_MANAGER]: true,
      [Support.INCIDENT_RESPONSE]: 'Business Day',
      [Support.INCIDENT_RESOLUTION_TIME]: false
    }
  },
  {
    name: 'Enterprise',
    description: 'Deploy an organization-wide platform for your current and future app roadmap',
    pricePerAppUserPerMonth: [40, 11],
    priceFootnote: 'Scaling based on user volume. Billed annually',
    startBuilding: false,
    capacities: {
      [Capacity.DEVELOPERS]: 'unlimited',
      [Capacity.NUMBER_OF_APPS]: 'unlimited',
      [Capacity.ENVIRONMENTS]: ['Dev', 'Staging', 'Production', 'Custom'],
      [Capacity.CLOUD_STORAGE_GB]: 'unlimited',
      [Capacity.CLOUD_REGIONS]: ['US', 'EU', 'APAC']
    },
    capabilities: {
      [Capability.FULLSTACK_DEVELOPMENT_PLATFORM]: true,
      [Capability.CLOUDCODE_SERVERLESS_INTEGRATIONS]: true,
      [Capability.POWERSYNC_INTEGRATION]: true,
      [Capability.STANDARD_SECURITY_FEATURES]: true,
      [Capability.CUSTOM_BRANDING]: true,
      [Capability.SSO_INTEGRATION]: true,
      [Capability.GITHUB_VERSION_CONTROL]: true,
      [Capability.CLOUDCODE_WEB_TASKS]: true,
      [Capability.NFC_ENABLED_APPS]: true,
      [Capability.SQL_DATA_PIPELINES]: true,
      [Capability.ADDITIONAL_SECURITY]: true,
      [Capability.ADVANCED_HARDWARE_CAPABILITIES]: true,
      [Capability.MODEL_VIEWER_3D]: true,
      [Capability.POWERBI_DASHBOARDS]: true,
      [Capability.FINE_GRAINED_PERMISSIONS]: true,
      [Capability.APP_PACKAGES]: true
    },
    support: {
      [Support.SLA]: true,
      [Support.ONBOARDING_SESSION]: true,
      [Support.DISCORD_SUPPORT]: true,
      [Support.DOCS]: true,
      [Support.HELPDESK]: true,
      [Support.CLOUD_UPTIME_GUARANTEE]: '99.95%',
      [Support.CUSTOMER_SUCCESS_MANAGER]: true,
      [Support.INCIDENT_RESPONSE]: '2 hours',
      [Support.INCIDENT_RESOLUTION_TIME]: true
    }
  }
];
